/**
 * What's New lifecycle.
 *
 * Automatic launch has two independent guards. Both are per signed-in user
 * and per release. Neither is a global "never show" flag, and neither is
 * stored in the database.
 *
 * 1. Same-session dismissal (sessionStorage):
 *      CP_WHATS_NEW_SESSION_DISMISSED:<userId> = "CREWPILOT_WHATS_NEW_1_0"
 *    Written when the user leaves the experience (Skip, Escape, hardware
 *    Back, Get to work). Survives a document reload in this browser/PWA
 *    session, including a service-worker controllerchange reload. A new
 *    browser session has no record, so the release may auto-present again.
 *
 * 2. Durable release suppression (localStorage):
 *      CP_WHATS_NEW_DISMISSED_RELEASE:<userId> = "CREWPILOT_WHATS_NEW_1_0"
 *    Written only by "Don't show again".
 *
 * User identity is supabase_user_id when present, otherwise id, so two
 * people on one device do not share a record. Shipping a new What's New
 * means changing WHATS_NEW_RELEASE: stored values no longer match, so the
 * new release is eligible again. Manual opening (Settings -> What's New)
 * consults neither guard.
 */
export const WHATS_NEW_RELEASE = "CREWPILOT_WHATS_NEW_1_0";
export const WHATS_NEW_DISMISSED_KEY = "CP_WHATS_NEW_DISMISSED_RELEASE";
export const WHATS_NEW_SESSION_DISMISSED_KEY = "CP_WHATS_NEW_SESSION_DISMISSED";
export const WHATS_NEW_OPEN_EVENT = "cp-whats-new-open";

function actorId(actor) {
  return actor?.supabase_user_id || actor?.id || null;
}

function storageKey(prefix, actor) {
  const id = actorId(actor);
  return id ? `${prefix}:${id}` : null;
}

function userKey(actor) {
  return storageKey(WHATS_NEW_DISMISSED_KEY, actor);
}

function sessionKey(actor) {
  return storageKey(WHATS_NEW_SESSION_DISMISSED_KEY, actor);
}

/** True when the user dismissed THIS release's automatic launch. */
export function isAutoLaunchSuppressed(actor, release = WHATS_NEW_RELEASE) {
  const key = userKey(actor);
  if (!key || typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(key) === release;
  } catch {
    return false;
  }
}

/** True when this user already left this release during the current browser session. */
export function isSessionDismissed(actor, release = WHATS_NEW_RELEASE) {
  const key = sessionKey(actor);
  if (!key || typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(key) === release;
  } catch {
    return false;
  }
}

/**
 * Record that this user left this release. Synchronous, so a reload that
 * follows immediately still sees the dismissal.
 */
export function setSessionDismissed(actor, release = WHATS_NEW_RELEASE) {
  const key = sessionKey(actor);
  if (!key || typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, release);
  } catch {
    // Storage blocked: the experience still closes, it just may show again.
  }
}

/** Automatic launch requires both guards to be clear for this user and release. */
export function isAutoLaunchEligible(actor, release = WHATS_NEW_RELEASE) {
  return !isAutoLaunchSuppressed(actor, release) && !isSessionDismissed(actor, release);
}

/** Suppress (or re-enable) the automatic launch of one release. */
export function setAutoLaunchSuppressed(actor, suppressed, release = WHATS_NEW_RELEASE) {
  const key = userKey(actor);
  if (!key || typeof window === "undefined") return;
  try {
    if (suppressed) window.localStorage.setItem(key, release);
    else if (window.localStorage.getItem(key) === release) window.localStorage.removeItem(key);
  } catch {
    // Storage blocked: the experience still works, it just may show again.
  }
}

/** Reopen from anywhere (Settings -> What's New). The host mounted in App listens. */
export function requestWhatsNew() {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new CustomEvent(WHATS_NEW_OPEN_EVENT));
}
